import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { CatalogueApi } from '@/features/catalogue';
import type { ClientsApi } from '@/features/clients';

import type { SchedulingApi } from './api';

/**
 * What the §6 staff scheduling screens need from outside React.
 *
 * The ability flags mirror the §5 permissions so the browser only draws what the caller
 * could actually do; the api enforces every one of them again (BP-04 R14 — a gate in the
 * browser is a courtesy, never the control).
 *
 * `ownStaffId` is §6's "technician sees own column only": the calendar filters its columns
 * to that one id when the signed-in user is a technician.
 */
export interface SchedulingDeps {
  api: SchedulingApi;
  /**
   * The slice of BP-02's catalogue the create modal needs: a booking item is a
   * `(service_id, staff_id, start_at)` and the operator picks the service by name and
   * duration. The dependency runs through `features/catalogue`'s **barrel**, which is the
   * one thing BP-00 R5 allows of a sibling feature — never a file inside it.
   */
  catalogue: Pick<CatalogueApi, 'listServices'>;
  /**
   * The slice of BP-03's client register the create modal needs. It returns the **masked**
   * number (`phone_masked`); a caller without `client.read_full` never sees more, and this
   * screen never asks for more.
   */
  clients: Pick<ClientsApi, 'listClients'>;
  /** `scheduling.write` — create, transition, reschedule. */
  canWrite: boolean;
  /** `settings.write` — the two settings screens. */
  canManageSettings: boolean;
  /** The staff row of the signed-in user, when they have one. */
  ownStaffId: string | null;
  /** True when the caller may see every column (frontdesk and up). */
  canSeeAllStaff: boolean;
  /** A fresh `Idempotency-Key` per click (ARCHITECTURE §3 API). Injected so a test is stable. */
  newIdempotencyKey: () => string;
  /** The tenant's IANA timezone, for the day boundaries the calendar draws. */
  timezone: string;
  /**
   * Whether the browser currently has a network.
   *
   * §6 gives the calendar an offline state and the staff-calendar intake
   * draws it: the day is read from the TanStack cache and every write is
   * disabled behind a banner. Reading a stale day is useful — it is still
   * today's bookings — and dragging one is not.
   */
  online: boolean;
  /**
   * Now, from the server (BP-05's rule, intake defect D6).
   *
   * The now-indicator and "next free" are the two things on this screen that
   * a wrong clock makes actively misleading, and a salon tablet's clock is
   * not something anybody checks. Injected so §11 can fix it.
   */
  now: () => Date;
}

const SchedulingContext = createContext<SchedulingDeps | null>(null);

export function SchedulingProvider({
  deps,
  children,
}: {
  deps: SchedulingDeps;
  children: ReactNode;
}): JSX.Element {
  return <SchedulingContext.Provider value={deps}>{children}</SchedulingContext.Provider>;
}

/** The scheduling dependencies. Throws when a screen is rendered outside the provider. */
export function useSchedulingDeps(): SchedulingDeps {
  const deps = useContext(SchedulingContext);
  if (deps === null) {
    throw new Error('26AD scheduling: this screen must be rendered inside <SchedulingProvider>');
  }
  return deps;
}

/** A random `Idempotency-Key`; `crypto.randomUUID` exists in every target browser. */
export function randomIdempotencyKey(): string {
  return crypto.randomUUID();
}
